/**
 * Persisted settings: boat profile, unit preferences, display options.
 *
 * localStorage rather than IndexedDB, deliberately. These are a handful of small
 * scalars read synchronously during first paint; routes and tracks are the
 * things that need IndexedDB, and they live in their own store.
 *
 * Every read is defensive. Settings survive app upgrades, so a stored shape from
 * an older version must never crash the app or — worse — silently produce a
 * boat profile with a zero draft.
 */

import {
  type BoatProfile,
  defaultBoatProfile,
  validateBoatProfile,
} from '../core/boat';
import {
  DEFAULT_UNITS,
  DEPTH_UNITS,
  DISTANCE_UNITS,
  SPEED_UNITS,
  VOLUME_UNITS,
  type UnitPreferences,
} from '../core/units';
import type { PaletteName } from '../chart/palette';
import { DEFAULT_AUTO_ROUTE_OPTIONS } from '../chart/autoRoute';
import { DEFAULT_PREDICTION_HORIZONS_S, PREDICTION_HORIZONS_S } from '../core/predict';
import { FORWARD_CONE_ANGLE_DEG } from '../core/forward';
import {
  type AlertStage,
  type CollisionSettings,
  type TintStyle,
  ALERT_STAGE_COUNT,
  COLLISION_LIMITS,
  DEFAULT_COLLISION,
  DEFAULT_ALERT_STAGES,
  MAX_STAGE_TIME_S,
  TINT_STYLES,
  normaliseStages,
} from '../core/proximity';

const STORAGE_KEY = 'webnav.settings.v1';

export interface Settings {
  boat: BoatProfile;
  units: UnitPreferences;
  palette: PaletteName;
  showSoundings: boolean;
  /** Place names and feature labels. */
  showLabels: boolean;
  /** Webcam markers. An app annotation, not chart data — src/core/webcams.ts. */
  showWebcams: boolean;
  /** Harbour markers linking to marinas.com — src/core/harbours.ts. */
  showHarbours: boolean;
  followVessel: boolean;
  keepScreenAwake: boolean;
  /** Dead-reckoning vector ahead of the boat. See src/core/predict.ts. */
  prediction: PredictionSettings;
  /**
   * Mark charted dangers lying in the sector ahead. See
   * src/chart/forwardDangers.ts — this is what replaced the always-on
   * isolated-danger symbol.
   */
  dangersAhead: boolean;
  /**
   * Depth at or below which water ahead is called out as shoal, metres, even
   * though the boat can float in it. Zero turns the second tier off.
   *
   * Separate from the safety depth on purpose. Ives Knoll is charted 2–5 m in
   * 20–30 m of harbour: for a dinghy drawing under a metre that is passable
   * water, so nothing marked it — and yet a named knoll with a beacon on it,
   * eight times shallower than everything around it, is worth seeing at fifteen
   * knots. The safety depth answers "can I float here"; this answers "is this
   * notably shoal", and the two are not the same question.
   */
  cautionDepthM: number;
  /**
   * Full width of the sector searched ahead, degrees — half either side of the
   * course.
   *
   * One number for everything the forward scan feeds: the marks on the chart,
   * the banner, the click track and the alert colour. Splitting it — a wide beam
   * to see with and a narrow one to be alarmed by — would put a magenta ring on
   * a rock the click track ignores, and a chart that disagrees with its own alarm
   * is worse than either alone.
   *
   * Narrower means fewer, more relevant marks and a quieter alarm; it also means
   * a rock just outside the beam is not mentioned at all. That trade is the
   * skipper's to make, which is why it is here and why the default is the wide
   * end of sensible.
   */
  coneAngleDeg: number;
  /**
   * Sound and colour raised by what the forward scan finds. Both off by
   * default — see src/core/proximity.ts.
   */
  alerts: AlertSettings;
  /** Route suggestion tuning. See src/chart/autoRoute.ts. */
  autoRoute: AutoRouteSettings;
  /** Developer aids. Off by default and loudly announced when on. */
  debug: DebugSettings;
  /**
   * Version of the disclaimer the skipper has acknowledged, or undefined if
   * never. A number rather than a boolean so that materially changing the
   * wording can require it to be read again — see src/core/legal.ts.
   */
  disclaimerAcknowledgedVersion?: number;
  /** Which settings tab was last open. Persisted so it reopens where you left. */
  settingsTab?: 'boat' | 'units' | 'routing' | 'display' | 'alerts' | 'debug' | 'legal';
}

/**
 * Simulated position, for looking at the chart somewhere the boat is not.
 *
 * This exists because the forward-danger cone is impossible to develop or
 * demonstrate from a desk: it needs a position, a course and a speed, and the
 * device sitting on a workbench in Halifax reports one position and no course
 * at all.
 *
 * `simulate` is persisted like every other preference, and that is a hazard in
 * itself — an app that quietly kept showing yesterday's pretend position would
 * be the worst possible failure of a navigation tool. The UI answers that by
 * announcing it in an undismissable banner for as long as it is on, and by
 * never letting a simulated fix look like a real one anywhere it is displayed.
 */
export interface DebugSettings {
  /** Tap the chart to move the simulated boat there. Overrides the GPS. */
  simulate: boolean;
  /** Course over ground the simulated boat reports, degrees true. */
  headingDeg: number;
  /** Speed over ground the simulated boat reports, metres per second. */
  speedMs: number;
  /**
   * Advance the simulated boat along its heading, once a second.
   *
   * A stationary simulated boat is enough to see the cone and the marks in it,
   * and useless for anything that changes as you close: the click rate and the
   * tint both only mean something while the range is shrinking. Tapping
   * progressively nearer a shoal is a poor substitute for standing towards it.
   */
  underway: boolean;
}

/**
 * The proximity alert: what the forward scan finds, made audible and visible.
 *
 * Both off by default. An app that started clicking at a skipper who never
 * asked for it would be turned off entirely, along with the parts of it that
 * matter. The level behind both is `src/core/proximity.ts`, so the sound and
 * the colour cannot disagree with each other or with the banner.
 */
export interface AlertSettings {
  /** The Geiger-style click track. */
  sound: boolean;
  /** Show the alert as a colour. */
  tint: boolean;
  /** Where that colour goes: a bar across the top, or the edges of the chart. */
  tintStyle: TintStyle;
  /**
   * When the alert reaches each strength, as time to run.
   *
   * The one curve behind everything: the click rate and the colour are both this
   * number rendered differently, so they cannot disagree. Editable because a
   * displacement boat at six knots and a RIB at thirty want different curves.
   * See src/core/proximity.ts.
   */
  stages: AlertStage[];
  /**
   * The collision alert: the last few seconds said as a flash rather than as
   * more of the same. Dangers only. See src/core/proximity.ts.
   */
  collision: CollisionSettings;
  /** 0–1. Applied squared, so the low end of the slider is usable. */
  volume: number;
  /**
   * Let shoal cautions raise the alert as well as dangers, capped at half.
   *
   * Off is the quieter setting, and the one for a shoal-strewn coast where the
   * caution tier is firing constantly.
   */
  includeCautions: boolean;
}

/** Fastest simulated speed offered: 40 kn covers any small craft. */
export const MAX_SIMULATED_SPEED_MS = 40 * (1852 / 3600);

/** Deepest water that can be called shoal: 30 m is past any coastal reckoning. */
export const MAX_CAUTION_DEPTH_M = 30;

/**
 * How narrow and how wide the forward beam may be set, degrees.
 *
 * The floor is 5°: at a mile that is still ±80 m either side of the course, which
 * is wider than a boat holds a course in a seaway. Anything narrower would be a
 * beam that a rock can sit beside while the boat runs onto it, and a warning that
 * misses by twenty metres reads as a warning that works.
 *
 * The ceiling is 90°. Past a quarter of the horizon the sector stops meaning "in
 * my path" and starts meaning "somewhere over there" — which is the always-on
 * danger symbol again, and the reason this feature exists.
 */
export const CONE_ANGLE_LIMITS = { min: 5, max: 90 } as const;

export interface PredictionSettings {
  /** Draw the vector at all. */
  enabled: boolean;
  /**
   * How far ahead to mark, in seconds. Empty means the skipper has turned every
   * horizon off, which is treated as no vector rather than as the defaults —
   * they said what they wanted.
   */
  horizonsS: number[];
}

export interface AutoRouteSettings {
  /**
   * Grid resolution in metres. Smaller finds narrower channels but costs
   * quadratically more to search.
   */
  cellSizeM: number;
  /** How far to stand off unnavigable water, in grid cells. */
  shoreClearanceCells: number;
}

export const AUTO_ROUTE_LIMITS = {
  cellSizeM: { min: 20, max: 200 },
  shoreClearanceCells: { min: 0, max: 6 },
} as const;

export function defaultSettings(): Settings {
  return {
    boat: defaultBoatProfile('default'),
    units: { ...DEFAULT_UNITS },
    palette: 'day',
    showSoundings: true,
    showLabels: true,
    showWebcams: true,
    showHarbours: true,
    followVessel: true,
    keepScreenAwake: true,
    prediction: {
      enabled: true,
      horizonsS: [...DEFAULT_PREDICTION_HORIZONS_S],
    },
    dangersAhead: true,
    // 5 m / about 16 ft: shoal by any small-craft reckoning, and shallow enough
    // that a harbour approach does not light up with it.
    cautionDepthM: 5,
    // The documented default, unchanged: about what a small craft wanders
    // through in a seaway while nominally steering straight. Narrowing this by
    // default would mean marking fewer dangers for everyone who never opens
    // settings, which is not a change to make on anyone else's behalf.
    coneAngleDeg: FORWARD_CONE_ANGLE_DEG,
    alerts: {
      sound: false,
      tint: false,
      tintStyle: 'bar',
      stages: DEFAULT_ALERT_STAGES.map((stage) => ({ ...stage })),
      collision: { ...DEFAULT_COLLISION },
      volume: 0.7,
      includeCautions: true,
    },
    autoRoute: {
      cellSizeM: DEFAULT_AUTO_ROUTE_OPTIONS.cellSizeM,
      shoreClearanceCells: DEFAULT_AUTO_ROUTE_OPTIONS.shoreClearanceCells,
    },
    debug: {
      simulate: false,
      headingDeg: 0,
      speedMs: 6 * (1852 / 3600), // 6 knots
      underway: false,
    },
    settingsTab: 'boat',
  };
}

/** Clamp a number into a range, falling back when it is not a number at all. */
const clamped = (value: unknown, min: number, max: number, fallback: number): number =>
  (typeof value === 'number' && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback);

const oneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  (typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback);

const boolOr = (value: unknown, fallback: boolean): boolean =>
  (typeof value === 'boolean' ? value : fallback);

const numberOr = (value: unknown, fallback: number): number =>
  (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

/**
 * Coerce arbitrary stored JSON into valid settings.
 *
 * A stored boat profile that fails validation is discarded entirely rather than
 * partially repaired: a half-restored profile could produce a safety depth that
 * looks plausible and is wrong, which is the one outcome worth avoiding.
 */
export function parseSettings(raw: unknown): Settings {
  const defaults = defaultSettings();
  if (typeof raw !== 'object' || raw === null) return defaults;
  const stored = raw as Record<string, unknown>;

  const storedBoat = (typeof stored.boat === 'object' && stored.boat !== null)
    ? (stored.boat as Record<string, unknown>)
    : {};

  const boat: BoatProfile = {
    ...defaults.boat,
    id: typeof storedBoat.id === 'string' ? storedBoat.id : defaults.boat.id,
    name: typeof storedBoat.name === 'string' && storedBoat.name.trim()
      ? storedBoat.name
      : defaults.boat.name,
    draftM: numberOr(storedBoat.draftM, defaults.boat.draftM),
    safetyMarginM: numberOr(storedBoat.safetyMarginM, defaults.boat.safetyMarginM),
    airDraftM: typeof storedBoat.airDraftM === 'number' && Number.isFinite(storedBoat.airDraftM)
      ? storedBoat.airDraftM
      : undefined,
    cruiseSpeedMs: numberOr(storedBoat.cruiseSpeedMs, defaults.boat.cruiseSpeedMs),
    fuelBurnLph: numberOr(storedBoat.fuelBurnLph, defaults.boat.fuelBurnLph),
    fuelCapacityL: typeof storedBoat.fuelCapacityL === 'number' && Number.isFinite(storedBoat.fuelCapacityL)
      ? storedBoat.fuelCapacityL
      : undefined,
    fuelReserveFraction: numberOr(storedBoat.fuelReserveFraction, defaults.boat.fuelReserveFraction),
  };

  const storedUnits = (typeof stored.units === 'object' && stored.units !== null)
    ? (stored.units as Record<string, unknown>)
    : {};

  return {
    boat: validateBoatProfile(boat).length === 0 ? boat : defaults.boat,
    units: {
      distance: oneOf(storedUnits.distance, DISTANCE_UNITS, defaults.units.distance),
      speed: oneOf(storedUnits.speed, SPEED_UNITS, defaults.units.speed),
      depth: oneOf(storedUnits.depth, DEPTH_UNITS, defaults.units.depth),
      volume: oneOf(storedUnits.volume, VOLUME_UNITS, defaults.units.volume),
    },
    palette: oneOf(stored.palette, ['day', 'night'] as const, defaults.palette),
    showSoundings: boolOr(stored.showSoundings, defaults.showSoundings),
    showLabels: boolOr(stored.showLabels, defaults.showLabels),
    showWebcams: boolOr(stored.showWebcams, defaults.showWebcams),
    showHarbours: boolOr(stored.showHarbours, defaults.showHarbours),
    followVessel: boolOr(stored.followVessel, defaults.followVessel),
    keepScreenAwake: boolOr(stored.keepScreenAwake, defaults.keepScreenAwake),
    prediction: parsePrediction(stored.prediction, defaults.prediction),
    dangersAhead: boolOr(stored.dangersAhead, defaults.dangersAhead),
    // Zero is meaningful — it turns the tier off — so the range starts there.
    // The ceiling stops a stored figure in feet from marking the whole shelf.
    cautionDepthM: clamped(stored.cautionDepthM, 0, MAX_CAUTION_DEPTH_M, defaults.cautionDepthM),
    // Clamped rather than defaulted: a stored 0 or 360 is a mistake, and the
    // nearest legal beam is a better answer than silently restoring the default
    // width the skipper had deliberately moved away from.
    coneAngleDeg: clamped(
      stored.coneAngleDeg,
      CONE_ANGLE_LIMITS.min,
      CONE_ANGLE_LIMITS.max,
      defaults.coneAngleDeg,
    ),
    alerts: parseAlerts(stored.alerts, defaults.alerts),
    autoRoute: parseAutoRoute(stored.autoRoute, defaults.autoRoute),
    debug: parseDebug(stored.debug, defaults.debug),
    // Anything that is not a finite number reads as "never acknowledged", so a
    // corrupted or hand-edited value shows the disclaimer again rather than
    // suppressing it. Erring towards showing it is the only safe direction.
    disclaimerAcknowledgedVersion:
      typeof stored.disclaimerAcknowledgedVersion === 'number'
        && Number.isFinite(stored.disclaimerAcknowledgedVersion)
        ? stored.disclaimerAcknowledgedVersion
        : undefined,
    settingsTab: oneOf(
      stored.settingsTab,
      ['boat', 'units', 'routing', 'display', 'alerts', 'debug', 'legal'] as const,
      'boat',
    ),
  };
}

/**
 * Restore the prediction settings.
 *
 * Horizons are filtered to the offered set rather than merely to "positive
 * numbers": a stored 86400 would draw a vector a day long, which at ten knots
 * reaches Bermuda and makes the chart unreadable. An empty list is kept as
 * empty — the skipper turning every horizon off is a choice, not corruption,
 * and quietly restoring the defaults would override it.
 */
function parsePrediction(raw: unknown, defaults: PredictionSettings): PredictionSettings {
  if (typeof raw !== 'object' || raw === null) return { ...defaults, horizonsS: [...defaults.horizonsS] };
  const stored = raw as Record<string, unknown>;

  const horizons = Array.isArray(stored.horizonsS)
    ? [...new Set(stored.horizonsS)]
      .filter((value): value is number => typeof value === 'number'
        && (PREDICTION_HORIZONS_S as readonly number[]).includes(value))
      .sort((a, b) => a - b)
    : [...defaults.horizonsS];

  return {
    enabled: boolOr(stored.enabled, defaults.enabled),
    horizonsS: horizons,
  };
}

/**
 * Restore the simulation settings.
 *
 * The heading is wrapped rather than clamped — 370° is 010°, and clamping it to
 * 360 would silently point the boat somewhere it was not asked to point. The
 * speed is clamped to a plausible small-craft range: a stored figure in knots
 * mistaken for metres per second would otherwise throw the cone miles ahead.
 */
function parseDebug(raw: unknown, defaults: DebugSettings): DebugSettings {
  const stored = (typeof raw === 'object' && raw !== null) ? (raw as Record<string, unknown>) : {};
  const heading = numberOr(stored.headingDeg, defaults.headingDeg);
  return {
    simulate: boolOr(stored.simulate, defaults.simulate),
    headingDeg: ((heading % 360) + 360) % 360,
    speedMs: clamped(stored.speedMs, 0, MAX_SIMULATED_SPEED_MS, defaults.speedMs),
    underway: boolOr(stored.underway, defaults.underway),
  };
}

/**
 * Restore the alert settings.
 *
 * Both switches default to off when the stored value is anything other than a
 * boolean, which is also what a settings blob written before this feature
 * existed produces. Silence and a plain chart are the safe restoration: an app
 * that came back from an upgrade making an unexpected noise would be muted at
 * the system level, taking the rest of its warnings with it.
 */
function parseAlerts(raw: unknown, defaults: AlertSettings): AlertSettings {
  const stored = (typeof raw === 'object' && raw !== null) ? (raw as Record<string, unknown>) : {};
  return {
    sound: boolOr(stored.sound, defaults.sound),
    tint: boolOr(stored.tint, defaults.tint),
    tintStyle: oneOf(stored.tintStyle, TINT_STYLES, defaults.tintStyle),
    stages: parseStages(stored.stages, defaults.stages),
    collision: parseCollision(stored.collision, defaults.collision),
    volume: clamped(stored.volume, 0, 1, defaults.volume),
    includeCautions: boolOr(stored.includeCautions, defaults.includeCautions),
  };
}

/**
 * Restore the alert curve.
 *
 * The count is pinned: the settings UI is a fixed grid of rows, and a stored
 * list of the wrong length would leave rows the skipper cannot see or edit —
 * silently changing when the alarm fires with no way to find out why. Anything
 * that is not exactly the expected shape falls back to the defaults whole, then
 * goes through `normaliseStages` so a curve that dips is repaired rather than
 * honoured. An alert that gets quieter as you close is worse than no alert.
 */
function parseStages(raw: unknown, defaults: AlertStage[]): AlertStage[] {
  if (!Array.isArray(raw) || raw.length !== ALERT_STAGE_COUNT) {
    return normaliseStages(defaults);
  }
  const stages = raw.map((entry, i) => {
    const stage = (typeof entry === 'object' && entry !== null)
      ? (entry as Record<string, unknown>)
      : {};
    return {
      timeToS: clamped(stage.timeToS, 0, MAX_STAGE_TIME_S, defaults[i].timeToS),
      level: clamped(stage.level, 0, 1, defaults[i].level),
    };
  });
  return normaliseStages(stages);
}

/**
 * Restore the collision alert.
 *
 * The flash rate is clamped into the band `COLLISION_LIMITS` allows, and that
 * ceiling is a photosensitive-seizure limit rather than a taste — see the note
 * there. A hand-edited or corrupted 20 Hz must come back as 3, not as 20.
 */
function parseCollision(raw: unknown, defaults: CollisionSettings): CollisionSettings {
  const stored = (typeof raw === 'object' && raw !== null) ? (raw as Record<string, unknown>) : {};
  return {
    enabled: boolOr(stored.enabled, defaults.enabled),
    timeToS: clamped(
      stored.timeToS,
      COLLISION_LIMITS.timeToS.min,
      COLLISION_LIMITS.timeToS.max,
      defaults.timeToS,
    ),
    flashHz: clamped(
      stored.flashHz,
      COLLISION_LIMITS.flashHz.min,
      COLLISION_LIMITS.flashHz.max,
      defaults.flashHz,
    ),
  };
}

function parseAutoRoute(raw: unknown, defaults: AutoRouteSettings): AutoRouteSettings {
  const stored = (typeof raw === 'object' && raw !== null) ? (raw as Record<string, unknown>) : {};
  return {
    cellSizeM: clamped(
      stored.cellSizeM,
      AUTO_ROUTE_LIMITS.cellSizeM.min,
      AUTO_ROUTE_LIMITS.cellSizeM.max,
      defaults.cellSizeM,
    ),
    shoreClearanceCells: clamped(
      stored.shoreClearanceCells,
      AUTO_ROUTE_LIMITS.shoreClearanceCells.min,
      AUTO_ROUTE_LIMITS.shoreClearanceCells.max,
      defaults.shoreClearanceCells,
    ),
  };
}

export function loadSettings(): Settings {
  if (typeof localStorage === 'undefined') return defaultSettings();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseSettings(JSON.parse(raw)) : defaultSettings();
  } catch {
    // Corrupt or unreadable storage must not stop the app from starting.
    return defaultSettings();
  }
}

/**
 * Persist the settings. Returns false if they could not be stored.
 *
 * The return value matters. Private browsing, a full quota and a browser set to
 * block site data all throw here, and swallowing that silently means the skipper
 * sets a draft, sees the chart re-shade, and gets the old draft back tomorrow —
 * with the chart re-shading to match, which looks like the app deciding on its
 * own that the boat draws less than it does. Losing a preference is not worth
 * interrupting navigation over; not *knowing* you have lost it is.
 */
export function saveSettings(settings: Settings): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    return true;
  } catch {
    return false;
  }
}
